import * as THREE from 'three';
import { lerp } from '../utils/helpers';
import { LANE_WIDTH, PLAYER_PHYSICS, AABB } from './constants';

/**
 * Robot player character with rich animations
 */
export class Player {
  public mesh: THREE.Group;

  // Movement state
  private velocityY = 0;
  private isJumping = false;
  private wasJumping = false;
  private currentLane = 1;
  private targetLaneX = 0;
  private previousX = 0;
  private currentGameSpeed = 15; // Track game speed for physics scaling

  // Animation state
  private animationTime = 0;
  private jumpPhase: 'none' | 'ascending' | 'peak' | 'descending' | 'landing' = 'none';
  private landingTimer = 0;
  private bodyParts: PlayerBodyParts;
  private visorMaterial: THREE.MeshLambertMaterial;
  private antennaTip: THREE.Mesh;

  constructor() {
    this.mesh = new THREE.Group();
    const { parts, visorMat, antennaTipMesh } = this.createRobot();
    this.bodyParts = parts;
    this.visorMaterial = visorMat;
    this.antennaTip = antennaTipMesh;
    this.mesh.position.set(0, PLAYER_PHYSICS.GROUND_Y, 0);
  }

  // ===========================================================================
  // Public Actions
  // ===========================================================================

  public jump(gameSpeed: number = 15): boolean {
    if (!this.isJumping) {
      this.currentGameSpeed = gameSpeed;
      
      // Scale jump velocity with game speed
      // Higher speed = faster jump (both up and down)
      const speedMultiplier = this.getJumpSpeedMultiplier();
      this.velocityY = PLAYER_PHYSICS.JUMP_FORCE * speedMultiplier;
      
      this.isJumping = true;
      this.jumpPhase = 'ascending';
      return true;
    }
    return false;
  }
  
  private getJumpSpeedMultiplier(): number {
    // Scale jump speed proportionally with game speed
    // At base speed (15), multiplier is 1.0
    // At speed 60 (insane max), multiplier is ~2.0
    return Math.sqrt(this.currentGameSpeed / 15);
  }

  public moveLeft(): void {
    if (this.currentLane > 0) {
      this.currentLane--;
      this.targetLaneX = (this.currentLane - 1) * LANE_WIDTH;
    }
  }

  public moveRight(): void {
    if (this.currentLane < 2) {
      this.currentLane++;
      this.targetLaneX = (this.currentLane - 1) * LANE_WIDTH;
    }
  }

  public update(deltaTime: number, gameSpeed: number): void {
    this.previousX = this.mesh.position.x;
    this.currentGameSpeed = gameSpeed; // Always track current speed
    this.updatePhysics(deltaTime);
    this.updateLanePosition(deltaTime);
    this.updateAnimation(deltaTime, gameSpeed);
  }

  public reset(): void {
    this.mesh.position.set(0, PLAYER_PHYSICS.GROUND_Y, 0);
    this.mesh.rotation.set(0, 0, 0);
    this.velocityY = 0;
    this.isJumping = false;
    this.wasJumping = false;
    this.currentLane = 1;
    this.targetLaneX = 0;
    this.previousX = 0;
    this.animationTime = 0;
    this.jumpPhase = 'none';
    this.landingTimer = 0;
  }

  public getBoundingBox(): AABB {
    const pos = this.mesh.position;
    const halfWidth = 0.25;
    const halfDepth = 0.15;
    const height = 1.4;

    return {
      minX: pos.x - halfWidth,
      maxX: pos.x + halfWidth,
      minY: pos.y - 0.5,
      maxY: pos.y - 0.5 + height,
      minZ: pos.z - halfDepth,
      maxZ: pos.z + halfDepth,
    };
  }

  // ===========================================================================
  // Physics
  // ===========================================================================

  private updatePhysics(deltaTime: number): void {
    this.wasJumping = this.isJumping;
    
    // Scale gravity with game speed - faster game = faster fall
    const speedMultiplier = this.getJumpSpeedMultiplier();
    const scaledGravity = PLAYER_PHYSICS.GRAVITY * speedMultiplier;
    
    this.velocityY += scaledGravity * deltaTime;
    this.mesh.position.y += this.velocityY * deltaTime;

    // Update jump phase (scale thresholds with speed)
    if (this.isJumping) {
      const threshold = 2 * speedMultiplier;
      if (this.velocityY > threshold) {
        this.jumpPhase = 'ascending';
      } else if (this.velocityY > -threshold) {
        this.jumpPhase = 'peak';
      } else {
        this.jumpPhase = 'descending';
      }
    }

    // Ground collision
    if (this.mesh.position.y <= PLAYER_PHYSICS.GROUND_Y) {
      this.mesh.position.y = PLAYER_PHYSICS.GROUND_Y;
      this.velocityY = 0;
      
      if (this.wasJumping) {
        this.jumpPhase = 'landing';
        this.landingTimer = 0.15;
      }
      
      this.isJumping = false;
    }

    // Landing timer
    if (this.landingTimer > 0) {
      this.landingTimer -= deltaTime;
      if (this.landingTimer <= 0) {
        this.jumpPhase = 'none';
      }
    }
  }

  private updateLanePosition(deltaTime: number): void {
    this.mesh.position.x = lerp(
      this.mesh.position.x,
      this.targetLaneX,
      PLAYER_PHYSICS.LANE_SWITCH_SPEED * deltaTime
    );
  }

  // ===========================================================================
  // Animation
  // ===========================================================================

  private updateAnimation(deltaTime: number, gameSpeed: number): void {
    // Animation speed scales gently with game speed, but stays reasonable
    const animationSpeedMultiplier = 0.5 + Math.min(gameSpeed / 40, 0.5);
    this.animationTime += deltaTime * animationSpeedMultiplier;
    
    this.animateBody(deltaTime);
    this.animateVisorGlow();
    this.animateAntenna();
    this.animateLeanOnMove();
  }

  private animateBody(_deltaTime: number): void {
    const { leftArm, rightArm, leftLeg, rightLeg, body, head, leftFoot, rightFoot } = this.bodyParts;
    const t = this.animationTime;

    if (this.jumpPhase !== 'none') {
      this.animateJump();
    } else {
      // === LUXURIOUS RUNNING ANIMATION ===
      // Balanced speed for smooth motion
      const runSpeed = 6; // 2x previous speed
      const cycle = t * runSpeed;
      
      // Primary motion - smooth sine wave for main limb movement
      const primaryPhase = Math.sin(cycle);
      const primaryPhaseOffset = Math.sin(cycle + Math.PI); // Opposite phase
      
      // Secondary motion - double frequency for bounce
      const secondaryPhase = Math.sin(cycle * 2);
      const secondaryAbs = Math.abs(secondaryPhase);
      
      // Tertiary motion - subtle high-frequency details
      const tertiaryPhase = Math.sin(cycle * 3);
      
      // === LEGS - Complex multi-joint simulation ===
      // Main leg swing with eased motion
      const legSwingAmount = 0.5;
      const legSwing = primaryPhase * legSwingAmount;
      const legSwingOpposite = primaryPhaseOffset * legSwingAmount;
      
      // Left leg - forward/back swing with lift
      leftLeg.rotation.x = legSwing;
      leftLeg.position.y = -0.22 + Math.max(0, primaryPhase) * 0.05; // Lift when forward
      leftLeg.position.z = primaryPhase * 0.03; // Slight forward motion
      
      // Right leg - opposite phase
      rightLeg.rotation.x = legSwingOpposite;
      rightLeg.position.y = -0.22 + Math.max(0, primaryPhaseOffset) * 0.05;
      rightLeg.position.z = primaryPhaseOffset * 0.03;
      
      // === FEET - Roll animation ===
      // Feet roll heel-to-toe during step
      const leftFootPhase = Math.max(0, primaryPhase);
      const rightFootPhase = Math.max(0, primaryPhaseOffset);
      
      leftFoot.rotation.x = leftFootPhase * 0.4 - 0.1; // Roll forward
      leftFoot.position.y = -0.5 + leftFootPhase * 0.03;
      
      rightFoot.rotation.x = rightFootPhase * 0.4 - 0.1;
      rightFoot.position.y = -0.5 + rightFootPhase * 0.03;
      
      // === ARMS - Natural swing with bend ===
      // Left arm swings opposite to left leg
      leftArm.rotation.x = -legSwing * 1.2; // Arms swing opposite to legs
      leftArm.rotation.z = 0.1 + secondaryAbs * 0.05; // Slight outward angle
      leftArm.position.y = 0.35 + tertiaryPhase * 0.01;
      
      // Right arm
      rightArm.rotation.x = -legSwingOpposite * 1.2;
      rightArm.rotation.z = -0.1 - secondaryAbs * 0.05;
      rightArm.position.y = 0.35 - tertiaryPhase * 0.01;
      
      // === BODY - Multi-axis movement ===
      // Vertical bounce (double frequency - bounces twice per step cycle)
      const bounce = secondaryAbs * 0.03;
      // Breathing animation (slow)
      const breathe = Math.sin(t * 1.5) * 0.008;
      // Body sway side to side
      const sway = primaryPhase * 0.015;
      // Twist with steps
      const twist = primaryPhase * 0.03;
      
      body.position.y = 0.35 + bounce + breathe;
      body.rotation.x = 0.08 + secondaryAbs * 0.02; // Forward lean increases with bounce
      body.rotation.y = twist; // Subtle twist
      body.rotation.z = sway; // Side sway
      
      // === HEAD - Independent motion with personality ===
      // Head bob (slightly delayed from body)
      const headBobPhase = Math.sin(cycle * 2 + 0.5);
      const headBob = Math.abs(headBobPhase) * 0.025;
      
      // Head looks around slightly
      const lookAround = Math.sin(t * 0.8) * 0.08;
      const lookUpDown = Math.sin(t * 1.2) * 0.03;
      
      head.position.y = 0.95 + headBob + breathe * 1.2;
      head.rotation.x = -0.05 + lookUpDown; // Slight look up/down
      head.rotation.y = lookAround; // Look left/right
      head.rotation.z = -sway * 0.5; // Counter-sway for stability look
    }
  }

  private animateJump(): void {
    const { leftArm, rightArm, leftLeg, rightLeg, body, head, leftFoot, rightFoot } = this.bodyParts;
    const t = this.animationTime;
    
    // Subtle air wobble for all jump phases
    const airWobble = Math.sin(t * 15) * 0.02;
    const airWobbleSlow = Math.sin(t * 8) * 0.03;
    
    switch (this.jumpPhase) {
      case 'ascending':
        // === POWERFUL LAUNCH POSE ===
        // Arms thrust back then swing up
        const ascendProgress = Math.min(1, this.velocityY / PLAYER_PHYSICS.JUMP_FORCE);
        
        leftArm.rotation.x = -1.3 - airWobble;
        leftArm.rotation.z = 0.4 + airWobbleSlow;
        leftArm.position.y = 0.35;
        
        rightArm.rotation.x = -1.3 + airWobble;
        rightArm.rotation.z = -0.4 - airWobbleSlow;
        rightArm.position.y = 0.35;
        
        // Legs tuck up dynamically
        const tuckAmount = 0.5 + ascendProgress * 0.3;
        leftLeg.rotation.x = tuckAmount;
        leftLeg.position.y = -0.22 + 0.05;
        rightLeg.rotation.x = tuckAmount - 0.1;
        rightLeg.position.y = -0.22 + 0.03;
        
        // Feet point down
        leftFoot.rotation.x = -0.4;
        leftFoot.position.y = -0.5;
        rightFoot.rotation.x = -0.5;
        rightFoot.position.y = -0.5;
        
        // Body leans back slightly with twist
        body.rotation.x = -0.2;
        body.rotation.y = airWobbleSlow * 0.5;
        body.rotation.z = airWobble;
        body.position.y = 0.35;
        
        // Head looks up
        head.rotation.x = -0.15;
        head.rotation.y = airWobbleSlow;
        head.position.y = 0.95;
        break;
        
      case 'peak':
        // === FLOATING APEX POSE ===
        // Arms spread wide like flying
        leftArm.rotation.x = -0.6 + airWobbleSlow;
        leftArm.rotation.z = 0.7;
        leftArm.position.y = 0.35 + 0.02;
        
        rightArm.rotation.x = -0.6 - airWobbleSlow;
        rightArm.rotation.z = -0.7;
        rightArm.position.y = 0.35 + 0.02;
        
        // Legs spread slightly, one forward one back
        leftLeg.rotation.x = 0.15 + airWobble;
        leftLeg.position.y = -0.22;
        rightLeg.rotation.x = -0.2 - airWobble;
        rightLeg.position.y = -0.22;
        
        // Feet relaxed
        leftFoot.rotation.x = -0.1 + airWobble;
        leftFoot.position.y = -0.5;
        rightFoot.rotation.x = 0.1 - airWobble;
        rightFoot.position.y = -0.5;
        
        // Body straight with subtle float
        body.rotation.x = airWobble * 2;
        body.rotation.y = airWobbleSlow;
        body.rotation.z = airWobble;
        body.position.y = 0.35 + Math.sin(t * 10) * 0.01;
        
        // Head level, looking forward
        head.rotation.x = airWobble;
        head.rotation.y = airWobbleSlow * 0.5;
        head.position.y = 0.95 + Math.sin(t * 12) * 0.01;
        break;
        
      case 'descending':
        // === PREPARE FOR LANDING POSE ===
        // Arms come forward for balance
        leftArm.rotation.x = 0.3 + airWobble;
        leftArm.rotation.z = 0.3;
        leftArm.position.y = 0.35;
        
        rightArm.rotation.x = 0.3 - airWobble;
        rightArm.rotation.z = -0.3;
        rightArm.position.y = 0.35;
        
        // Legs extend down to prepare
        leftLeg.rotation.x = 0.4 + airWobble;
        leftLeg.position.y = -0.22 - 0.02;
        rightLeg.rotation.x = 0.4 - airWobble;
        rightLeg.position.y = -0.22 - 0.02;
        
        // Feet angle for landing
        leftFoot.rotation.x = 0.3;
        leftFoot.position.y = -0.5 - 0.02;
        rightFoot.rotation.x = 0.3;
        rightFoot.position.y = -0.5 - 0.02;
        
        // Body leans forward
        body.rotation.x = 0.15;
        body.rotation.y = 0;
        body.rotation.z = airWobble;
        body.position.y = 0.35;
        
        // Head looks down at landing spot
        head.rotation.x = 0.2;
        head.rotation.y = 0;
        head.position.y = 0.95;
        break;
        
      case 'landing':
        // === IMPACT SQUASH & RECOVERY ===
        const landProgress = this.landingTimer / 0.15; // 1 to 0
        const squashCurve = Math.sin(landProgress * Math.PI); // Smooth squash curve
        const squashAmount = squashCurve * 0.12;
        const spreadAmount = squashCurve * 0.2;
        
        // Arms swing forward and out for balance
        leftArm.rotation.x = 0.6 * squashCurve;
        leftArm.rotation.z = 0.4 + spreadAmount;
        leftArm.position.y = 0.35 - squashAmount * 0.3;
        
        rightArm.rotation.x = 0.6 * squashCurve;
        rightArm.rotation.z = -0.4 - spreadAmount;
        rightArm.position.y = 0.35 - squashAmount * 0.3;
        
        // Legs bend on impact
        const legBend = 0.4 * squashCurve;
        leftLeg.rotation.x = legBend;
        leftLeg.position.y = -0.22 + squashAmount * 0.5;
        rightLeg.rotation.x = legBend;
        rightLeg.position.y = -0.22 + squashAmount * 0.5;
        
        // Feet flatten
        leftFoot.rotation.x = -0.2 * squashCurve;
        leftFoot.position.y = -0.5 + squashAmount * 0.3;
        rightFoot.rotation.x = -0.2 * squashCurve;
        rightFoot.position.y = -0.5 + squashAmount * 0.3;
        
        // Body compresses
        body.position.y = 0.35 - squashAmount;
        body.rotation.x = 0.15 * squashCurve;
        body.rotation.z = 0;
        body.rotation.y = 0;
        
        // Head dips with impact
        head.position.y = 0.95 - squashAmount * 0.6;
        head.rotation.x = 0.1 * squashCurve;
        break;
    }
  }

  private animateVisorGlow(): void {
    const t = this.animationTime;
    
    // === DYNAMIC VISOR GLOW ===
    // Base breathing pulse
    const breathePulse = Math.sin(t * 2) * 0.1;
    // Faster flicker for tech feel
    const techFlicker = Math.sin(t * 15) * 0.05 + Math.sin(t * 23) * 0.03;
    // Heartbeat-like double pulse
    const heartbeat = (Math.sin(t * 4) > 0.7 ? 0.15 : 0) + (Math.sin(t * 4 + 0.3) > 0.8 ? 0.1 : 0);
    
    let baseGlow = 0.4 + breathePulse + techFlicker + heartbeat;
    
    // State-based glow changes
    if (this.jumpPhase === 'ascending') {
      // Bright burst on jump
      baseGlow = 0.9 + Math.sin(t * 30) * 0.1;
    } else if (this.jumpPhase === 'peak') {
      // Steady bright at peak
      baseGlow = 0.75 + Math.sin(t * 20) * 0.05;
    } else if (this.jumpPhase === 'descending') {
      // Pulsing as falling
      baseGlow = 0.6 + Math.sin(t * 25) * 0.15;
    } else if (this.jumpPhase === 'landing') {
      // Flash on impact
      const impact = this.landingTimer / 0.15;
      baseGlow = 0.5 + impact * 0.5;
    }
    
    // Clamp and apply
    this.visorMaterial.emissiveIntensity = Math.max(0.2, Math.min(1.0, baseGlow));
  }

  private animateAntenna(): void {
    const { antenna } = this.bodyParts;
    const t = this.animationTime;
    
    // === EXPRESSIVE ANTENNA PHYSICS ===
    // Base spring motion
    const springX = Math.sin(t * 6) * 0.08;
    const springZ = Math.sin(t * 7 + 1) * 0.06;
    
    // Movement reaction - antenna lags behind head movement
    const moveReaction = (this.mesh.position.x - this.previousX) * 3;
    
    // Jump reaction - more dramatic wobble
    let jumpReaction = 0;
    let jumpBounce = 0;
    if (this.jumpPhase === 'ascending') {
      jumpReaction = Math.sin(t * 25) * 0.2;
      jumpBounce = -0.1;
    } else if (this.jumpPhase === 'landing') {
      const impact = this.landingTimer / 0.15;
      jumpReaction = Math.sin(t * 30) * 0.25 * impact;
      jumpBounce = 0.15 * impact;
    } else if (this.isJumping) {
      jumpReaction = Math.sin(t * 18) * 0.12;
    }
    
    // Combine all antenna motion
    antenna.rotation.x = springX + jumpReaction + jumpBounce;
    antenna.rotation.z = springZ - moveReaction;
    
    // Antenna tip has even more bounce (like a bobblehead)
    const tipBounceX = Math.sin(t * 10) * 0.03;
    const tipBounceY = Math.sin(t * 12) * 0.025;
    const tipJumpBounce = this.isJumping ? Math.sin(t * 20) * 0.04 : 0;
    
    this.antennaTip.position.x = tipBounceX + moveReaction * 0.02;
    this.antennaTip.position.y = 1.4 + tipBounceY + tipJumpBounce;
    this.antennaTip.position.z = Math.sin(t * 8) * 0.015;
  }

  private animateLeanOnMove(): void {
    // Lean into the direction of movement
    const moveSpeed = this.mesh.position.x - this.previousX;
    const targetLean = -moveSpeed * 2;
    
    // Smooth lean
    this.mesh.rotation.z = lerp(this.mesh.rotation.z, Math.max(-0.15, Math.min(0.15, targetLean)), 0.1);
  }

  // ===========================================================================
  // Robot Creation
  // ===========================================================================

  private createRobot(): { parts: PlayerBodyParts; visorMat: THREE.MeshLambertMaterial; antennaTipMesh: THREE.Mesh } {
    const materials = this.createMaterials();
    
    const body = this.createBody(materials.body);
    const head = this.createHead(materials.accent);
    const visor = this.createVisor(materials.visor);
    const { antenna, antennaTip } = this.createAntenna(materials.dark, materials.visor);
    const { leftArm, rightArm } = this.createArms(materials.dark);
    const { leftLeg, rightLeg } = this.createLegs(materials.dark);
    const { leftFoot, rightFoot } = this.createFeet(materials.body);

    return {
      parts: { body, head, leftArm, rightArm, leftLeg, rightLeg, antenna, visor, leftFoot, rightFoot },
      visorMat: materials.visor,
      antennaTipMesh: antennaTip,
    };
  }

  private createMaterials() {
    const colors = {
      body: 0x5c8a9e,
      accent: 0x7ec8e3,
      dark: 0x2d4a5e,
      visor: 0x00ffaa,
    };

    return {
      body: new THREE.MeshLambertMaterial({ color: colors.body, flatShading: true }),
      accent: new THREE.MeshLambertMaterial({ color: colors.accent, flatShading: true }),
      dark: new THREE.MeshLambertMaterial({ color: colors.dark, flatShading: true }),
      visor: new THREE.MeshLambertMaterial({
        color: colors.visor,
        emissive: colors.visor,
        emissiveIntensity: 0.5,
        flatShading: true,
      }),
    };
  }

  private createBody(material: THREE.Material): THREE.Mesh {
    const geom = new THREE.BoxGeometry(0.6, 0.7, 0.4);
    const mesh = new THREE.Mesh(geom, material);
    mesh.position.y = 0.35;
    this.mesh.add(mesh);
    return mesh;
  }

  private createHead(material: THREE.Material): THREE.Mesh {
    const geom = new THREE.BoxGeometry(0.5, 0.45, 0.4);
    const mesh = new THREE.Mesh(geom, material);
    mesh.position.y = 0.95;
    this.mesh.add(mesh);
    return mesh;
  }

  private createVisor(material: THREE.Material): THREE.Mesh {
    const geom = new THREE.BoxGeometry(0.4, 0.12, 0.05);
    const mesh = new THREE.Mesh(geom, material);
    mesh.position.set(0, 0.95, 0.2);
    this.mesh.add(mesh);
    return mesh;
  }

  private createAntenna(darkMaterial: THREE.Material, visorMaterial: THREE.Material): { antenna: THREE.Mesh; antennaTip: THREE.Mesh } {
    const antennaGeom = new THREE.CylinderGeometry(0.03, 0.03, 0.2, 6);
    const antenna = new THREE.Mesh(antennaGeom, darkMaterial);
    antenna.position.set(0, 1.28, 0);
    this.mesh.add(antenna);

    const tipGeom = new THREE.SphereGeometry(0.06, 6, 4);
    const antennaTip = new THREE.Mesh(tipGeom, visorMaterial);
    antennaTip.position.set(0, 1.4, 0);
    this.mesh.add(antennaTip);

    return { antenna, antennaTip };
  }

  private createArms(material: THREE.Material): { leftArm: THREE.Mesh; rightArm: THREE.Mesh } {
    const geom = new THREE.BoxGeometry(0.15, 0.5, 0.15);

    const leftArm = new THREE.Mesh(geom, material);
    leftArm.position.set(-0.45, 0.35, 0);
    this.mesh.add(leftArm);

    const rightArm = new THREE.Mesh(geom, material);
    rightArm.position.set(0.45, 0.35, 0);
    this.mesh.add(rightArm);

    return { leftArm, rightArm };
  }

  private createLegs(material: THREE.Material): { leftLeg: THREE.Mesh; rightLeg: THREE.Mesh } {
    const geom = new THREE.BoxGeometry(0.18, 0.45, 0.18);

    const leftLeg = new THREE.Mesh(geom, material);
    leftLeg.position.set(-0.18, -0.22, 0);
    this.mesh.add(leftLeg);

    const rightLeg = new THREE.Mesh(geom, material);
    rightLeg.position.set(0.18, -0.22, 0);
    this.mesh.add(rightLeg);

    return { leftLeg, rightLeg };
  }

  private createFeet(material: THREE.Material): { leftFoot: THREE.Mesh; rightFoot: THREE.Mesh } {
    const geom = new THREE.BoxGeometry(0.2, 0.1, 0.25);

    const leftFoot = new THREE.Mesh(geom, material);
    leftFoot.position.set(-0.18, -0.5, 0.03);
    this.mesh.add(leftFoot);

    const rightFoot = new THREE.Mesh(geom, material);
    rightFoot.position.set(0.18, -0.5, 0.03);
    this.mesh.add(rightFoot);

    return { leftFoot, rightFoot };
  }
}

// ===========================================================================
// Types
// ===========================================================================

interface PlayerBodyParts {
  body: THREE.Mesh;
  head: THREE.Mesh;
  leftArm: THREE.Mesh;
  rightArm: THREE.Mesh;
  leftLeg: THREE.Mesh;
  rightLeg: THREE.Mesh;
  leftFoot: THREE.Mesh;
  rightFoot: THREE.Mesh;
  antenna: THREE.Mesh;
  visor: THREE.Mesh;
}
